/**
 * Die Darstellung am Konto — hell/dunkel folgt der Person auf jedes Gerät.
 *
 * Zwei Speicher, eine Wahl (Entscheidung 07.10.2026):
 *  - der Cookie (`shared/Darstellung.tsx`, ein Jahr): Quelle für das erste HTML
 *    und die einzige Quelle für Besucher ohne Konto;
 *  - `users.darstellung` ('light'/'dark', NULL = nie gewählt) für Angemeldete.
 *
 * Nach der Anmeldung übernimmt der Browser den Kontowert in den Cookie; jedes
 * Umschalten danach schreibt ihn ins Konto zurück. Ein Konto ohne Wahl ändert
 * nichts am Browser — erst ein Klick legt sie fest. Während einer Impersonation
 * gilt weder das eine noch das andere: Die Darstellung des Admins bleibt, und ins
 * fremde Konto wird nichts geschrieben.
 *
 * Der Schalter selbst ist ein Fundament-Baustein und kennt kein Konto; er meldet
 * jeden Wechsel als Fenster-Ereignis, und hier wird darauf gehört.
 */
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { authAPI } from '../services/api';
import { DARSTELLUNG_EREIGNIS, istDunkel, setzeDunkel } from '../shared/Darstellung';
import type { components } from '../types/api.generated';
import { DARSTELLUNG_COOKIE } from '../utils/darstellungCookie';
import { fehlerText } from '../utils/fehlerText';

type Nutzer = components['schemas']['UserResponse'];
type Darstellung = NonNullable<Nutzer['darstellung']>;

export default function DarstellungKonto() {
  // Erst die bestätigte Sitzung — `user` ist bis zur Antwort von /me der Cache aus dem localStorage.
  const { sitzungsNutzer: user, isImpersonating } = useAuth() as { sitzungsNutzer: Nutzer | null; isImpersonating: boolean };
  const { t } = useTranslation();
  const [fehler, setFehler] = useState<unknown>(null);
  // undefined = niemand angemeldet (dann wird nichts gespeichert), null = Konto ohne Wahl.
  const kontoWert = useRef<Darstellung | null | undefined>(undefined);

  const nutzerId: number | undefined = user?.id;
  const gespeichert: Darstellung | null = user?.darstellung ?? null;

  useEffect(() => {
    if (nutzerId === undefined || isImpersonating) {
      kontoWert.current = undefined;
      return;
    }
    kontoWert.current = gespeichert;
    if (gespeichert && (gespeichert === 'dark') !== istDunkel()) {
      setzeDunkel(DARSTELLUNG_COOKIE, gespeichert === 'dark');
    }
  }, [nutzerId, gespeichert, isImpersonating]);

  useEffect(() => {
    function gewechselt(ereignis: Event) {
      if (kontoWert.current === undefined) return;
      const wert: Darstellung = (ereignis as CustomEvent<boolean>).detail ? 'dark' : 'light';
      if (wert === kontoWert.current) return;
      const vorher = kontoWert.current;
      kontoWert.current = wert;
      authAPI.updateSettings({ darstellung: wert })
        .then(() => setFehler(null))
        .catch((err: unknown) => {
          kontoWert.current = vorher;
          console.error('Darstellung konnte nicht im Konto gespeichert werden', err);
          setFehler(err);
        });
    }
    window.addEventListener(DARSTELLUNG_EREIGNIS, gewechselt);
    return () => window.removeEventListener(DARSTELLUNG_EREIGNIS, gewechselt);
  }, []);

  if (!fehler) return null;
  return (
    <div
      role="alert"
      className="alert alert-error"
      style={{ position: 'fixed', bottom: 16, left: 16, right: 16, maxWidth: 480, margin: '0 auto', zIndex: 60, display: 'flex', gap: 12, alignItems: 'flex-start' }}
    >
      <span style={{ flex: 1 }}>
        {t('darstellung.speichern_fehler', 'Deine Darstellung konnte nicht im Konto gespeichert werden: {grund}', { grund: fehlerText(fehler, t) })}
      </span>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setFehler(null)}>
        {t('common.close', 'Schließen')}
      </button>
    </div>
  );
}
